import { notificationConstants as C } from "../actions/constants";
import {
    emptyCategoryCounts,
    getNotificationCategory,
    normalizeCategoryCounts,
} from "../utils/notificationCategory";

const initialState = {
    notifications: [],
    unread: {
        personal: 0,
        // Backward-compatible alias for total office unread.
        office: 0,
        officeTotal: 0,
        byOffice: {},
        // Непрочитанные по разделам — с сервера (/notification/counts).
        byCategory: {
            personal: emptyCategoryCounts(),
            office: emptyCategoryCounts(),
        },
    },
    realtimeIds: {},
    loading: false,
    countsLoading: false,
    error: null,
    hasMore: true,
    nextCursor: null,
    filters: {
        scope: "personal",
        officeId: null,
        limit: 15,
        sort: "desc",
        from: null,
        to: null,
        category: "",
    },
};

const getId = (item) => item?._id || item?.id;

const getOfficeId = (item) => String(
    item?.office?._id ||
    item?.office?.id ||
    item?.office ||
    item?.officeId ||
    item?.meta?.officeId ||
    ""
).trim();

const normalizeNotification = (item = {}) => {
    const scope = item.scope || item.meta?.scope || "personal";
    const id = getId(item);

    return {
        ...item,
        _id: item._id || id,
        id: item.id || id,
        scope,
        isRead: !!item.isRead,
        createdAt: item.createdAt || new Date().toISOString(),
        recipients: item.recipients || [],
        readBy: item.readBy || [],
    };
};

const trimRealtimeIds = (map = {}) => {
    const entries = Object.entries(map);

    if (entries.length <= 500) return map;

    return Object.fromEntries(entries.slice(entries.length - 500));
};

const mergeUniqueById = (current = [], incoming = [], prepend = false) => {
    const map = new Map();

    const first = prepend ? incoming : current;
    const second = prepend ? current : incoming;

    first.forEach((item) => {
        const id = getId(item);

        if (id) map.set(String(id), normalizeNotification(item));
    });

    second.forEach((item) => {
        const id = getId(item);

        if (id && !map.has(String(id))) {
            map.set(String(id), normalizeNotification(item));
        }
    });

    return Array.from(map.values()).sort((a, b) => {
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    });
};

const normalizeByOffice = (value = {}) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};

    return Object.fromEntries(
        Object.entries(value).map(([officeId, count]) => [String(officeId), Math.max(Number(count || 0), 0)])
    );
};

const withOfficeUnread = (unread = {}, officeTotal, byOffice) => {
    const safeByOffice = normalizeByOffice(byOffice);
    const safeTotal = Math.max(Number(officeTotal || 0), 0);

    return {
        ...unread,
        office: safeTotal,
        officeTotal: safeTotal,
        byOffice: safeByOffice,
    };
};

const getByCategory = (unread = {}) => ({
    personal: normalizeCategoryCounts(unread?.byCategory?.personal),
    office: normalizeCategoryCounts(unread?.byCategory?.office),
});

const shiftCategory = (unread = {}, scope, category, delta) => {
    const byCategory = getByCategory(unread);
    const key = scope === "office" ? "office" : "personal";

    if (!category || byCategory[key][category] === undefined) return byCategory;

    byCategory[key] = {
        ...byCategory[key],
        [category]: Math.max(Number(byCategory[key][category] || 0) + delta, 0),
    };

    return byCategory;
};

const markOneRead = (state, id, scope = "personal", explicitOfficeId = null) => {
    let found = false;
    let changed = false;
    let matchedOfficeId = explicitOfficeId ? String(explicitOfficeId) : "";
    let matchedCategory = "";

    const notifications = state.notifications.map((item) => {
        if (String(getId(item)) !== String(id)) return item;

        found = true;
        if (!matchedOfficeId) matchedOfficeId = getOfficeId(item);
        matchedCategory = getNotificationCategory(item);
        if (item.isRead) return item;

        changed = true;

        return {
            ...item,
            isRead: true,
        };
    });

    const byCategory = changed
        ? shiftCategory(state.unread, scope, matchedCategory, -1)
        : getByCategory(state.unread);

    if (scope !== "office") {
        const shouldDecrease = changed || !found;

        return {
            ...state,
            notifications,
            unread: {
                ...state.unread,
                byCategory,
                personal: shouldDecrease
                    ? Math.max(Number(state.unread?.personal || 0) - 1, 0)
                    : Number(state.unread?.personal || 0),
            },
        };
    }

    const shouldDecrease = changed || !found;
    const currentByOffice = normalizeByOffice(state.unread?.byOffice || {});
    const nextByOffice = { ...currentByOffice };
    let nextOfficeTotal = Number(state.unread?.officeTotal ?? state.unread?.office ?? 0);

    if (shouldDecrease) {
        nextOfficeTotal = Math.max(nextOfficeTotal - 1, 0);

        if (matchedOfficeId) {
            nextByOffice[matchedOfficeId] = Math.max(Number(nextByOffice[matchedOfficeId] || 0) - 1, 0);
        }
    }

    return {
        ...state,
        notifications,
        unread: withOfficeUnread({ ...state.unread, byCategory }, nextOfficeTotal, nextByOffice),
    };
};

/**
 * «Прочитать все» в одном разделе: отмечаем только его, итог уменьшаем на
 * число непрочитанных в разделе. Точные цифры потом придут с /notification/counts.
 */
const markCategoryRead = (state, scope, category, officeId = null) => {
    const key = scope === "office" ? "office" : "personal";
    const cleanOfficeId = officeId ? String(officeId) : "";
    const byCategory = getByCategory(state.unread);
    const removed = Number(byCategory[key][category] || 0);

    const notifications = state.notifications.map((item) => {
        const itemScope = item.scope === "office" ? "office" : "personal";
        if (itemScope !== key || item.isRead) return item;
        if (getNotificationCategory(item) !== category) return item;
        if (cleanOfficeId && getOfficeId(item) !== cleanOfficeId) return item;
        return { ...item, isRead: true };
    });

    byCategory[key] = { ...byCategory[key], [category]: 0 };

    if (key === "personal") {
        return {
            ...state,
            notifications,
            unread: {
                ...state.unread,
                byCategory,
                personal: Math.max(Number(state.unread?.personal || 0) - removed, 0),
            },
        };
    }

    const currentTotal = Number(state.unread?.officeTotal ?? state.unread?.office ?? 0);

    return {
        ...state,
        notifications,
        unread: withOfficeUnread(
            { ...state.unread, byCategory },
            Math.max(currentTotal - removed, 0),
            state.unread?.byOffice || {}
        ),
    };
};

const markScopeRead = (state, scope = "personal", officeId = null, category = null) => {
    if (category) return markCategoryRead(state, scope, category, officeId);

    const clearedByCategory = {
        ...getByCategory(state.unread),
        [scope === "office" ? "office" : "personal"]: emptyCategoryCounts(),
    };

    if (scope !== "office") {
        return {
            ...state,
            notifications: state.notifications.map((item) => (
                item.scope === "personal"
                    ? { ...item, isRead: true }
                    : item
            )),
            unread: {
                ...state.unread,
                byCategory: clearedByCategory,
                personal: 0,
            },
        };
    }

    const cleanOfficeId = officeId ? String(officeId) : "";
    const currentByOffice = normalizeByOffice(state.unread?.byOffice || {});

    if (!cleanOfficeId) {
        return {
            ...state,
            notifications: state.notifications.map((item) => (
                item.scope === "office"
                    ? { ...item, isRead: true }
                    : item
            )),
            unread: withOfficeUnread({ ...state.unread, byCategory: clearedByCategory }, 0, Object.fromEntries(
                Object.keys(currentByOffice).map((id) => [id, 0])
            )),
        };
    }

    const removed = Number(currentByOffice[cleanOfficeId] || 0);
    const nextByOffice = {
        ...currentByOffice,
        [cleanOfficeId]: 0,
    };
    const currentTotal = Number(state.unread?.officeTotal ?? state.unread?.office ?? 0);

    return {
        ...state,
        notifications: state.notifications.map((item) => (
            item.scope === "office" && getOfficeId(item) === cleanOfficeId
                ? { ...item, isRead: true }
                : item
        )),
        unread: withOfficeUnread(
            state.unread,
            Math.max(currentTotal - removed, 0),
            nextByOffice
        ),
    };
};

export default function notificationReducer(state = initialState, action) {
    switch (action.type) {
        case C.SET_FILTERS:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    ...(action.payload || {}),
                },
            };

        case C.RESET_LIST:
            return {
                ...state,
                notifications: [],
                hasMore: true,
                nextCursor: null,
                loading: false,
                error: null,
            };

        case C.FETCH_PAGE_REQUEST:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case C.FETCH_PAGE_SUCCESS: {
            const incoming = (action.payload.items || []).map(normalizeNotification);
            const scope = action.payload.scope === "office" ? "office" : "personal";
            const unread = action.payload.unread || {};
            let nextUnread = { ...state.unread };
            const nextRealtimeIds = { ...state.realtimeIds };

            incoming.forEach((item) => {
                const id = getId(item);

                if (id) {
                    nextRealtimeIds[String(id)] = true;
                }
            });

            if (scope === "office") {
                const officeTotal = unread.officeTotal !== undefined
                    ? Number(unread.officeTotal || 0)
                    : Number(unread.office || 0);
                const byOffice = unread.byOffice && typeof unread.byOffice === "object"
                    ? unread.byOffice
                    : state.unread?.byOffice || {};

                nextUnread = withOfficeUnread(nextUnread, officeTotal, byOffice);
            }

            if (scope === "personal" && unread.personal !== undefined) {
                nextUnread.personal = Number(unread.personal || 0);
            }

            return {
                ...state,
                notifications: mergeUniqueById(state.notifications, incoming, false),
                nextCursor: action.payload.nextCursor || null,
                hasMore: action.payload.hasMore === true,
                loading: false,
                unread: nextUnread,
                realtimeIds: trimRealtimeIds(nextRealtimeIds),
            };
        }

        case C.FETCH_PAGE_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload?.error || "Ошибка загрузки уведомлений",
            };

        case C.FETCH_COUNTS_REQUEST:
            return {
                ...state,
                countsLoading: true,
            };

        case C.FETCH_COUNTS_SUCCESS: {
            const personal = Number(action.payload?.personal || 0);
            const officeTotal = Number(action.payload?.officeTotal ?? action.payload?.office ?? 0);
            const byOffice = normalizeByOffice(action.payload?.byOffice || {});
            const serverByCategory = action.payload?.byCategory;

            return {
                ...state,
                countsLoading: false,
                unread: withOfficeUnread({
                    ...state.unread,
                    personal,
                    byCategory: serverByCategory
                        ? {
                            personal: normalizeCategoryCounts(serverByCategory.personal),
                            office: normalizeCategoryCounts(serverByCategory.office),
                        }
                        : getByCategory(state.unread),
                }, officeTotal, byOffice),
            };
        }

        case C.FETCH_COUNTS_FAILURE:
            return {
                ...state,
                countsLoading: false,
                error: action.payload?.error || "Ошибка загрузки количества уведомлений",
            };

        case C.RECEIVED: {
            const notification = normalizeNotification(action.payload);
            const notificationId = getId(notification);

            if (!notificationId) return state;

            const idKey = String(notificationId);
            const alreadyReceived = !!state.realtimeIds[idKey];
            const existsInList = state.notifications.some((item) => String(getId(item)) === idKey);

            if (alreadyReceived || existsInList) {
                return {
                    ...state,
                    realtimeIds: trimRealtimeIds({
                        ...state.realtimeIds,
                        [idKey]: true,
                    }),
                };
            }

            const scope = notification.scope === "office" ? "office" : "personal";
            let nextUnread = { ...state.unread };

            if (!notification.isRead) {
                nextUnread.byCategory = shiftCategory(
                    state.unread,
                    scope,
                    getNotificationCategory(notification),
                    1
                );

                if (scope === "office") {
                    const officeId = getOfficeId(notification);
                    const byOffice = normalizeByOffice(state.unread?.byOffice || {});
                    const nextByOffice = { ...byOffice };

                    if (officeId) {
                        nextByOffice[officeId] = Number(nextByOffice[officeId] || 0) + 1;
                    }

                    nextUnread = withOfficeUnread(
                        nextUnread,
                        Number(state.unread?.officeTotal ?? state.unread?.office ?? 0) + 1,
                        nextByOffice
                    );
                } else {
                    nextUnread.personal = Number(state.unread?.personal || 0) + 1;
                }
            }

            return {
                ...state,
                notifications: mergeUniqueById(state.notifications, [notification], true),
                unread: nextUnread,
                realtimeIds: trimRealtimeIds({
                    ...state.realtimeIds,
                    [idKey]: true,
                }),
            };
        }

        case C.NOTIFICATION_MARK_READ: {
            const id = action.payload?.id || action.payload;
            const scope = action.payload?.scope === "office" ? "office" : "personal";

            return markOneRead(state, id, scope, action.payload?.officeId || null);
        }

        case C.NOTIFICATION_MARK_ALL_READ: {
            const scope = action.payload?.scope === "office" ? "office" : "personal";

            return markScopeRead(state, scope, action.payload?.officeId || null, action.payload?.category || null);
        }

        case C.NOTIFICATION_REMOTE_READ: {
            const id = action.payload?.id || action.payload;
            const scope = action.payload?.scope === "office" ? "office" : "personal";

            return markOneRead(state, id, scope, action.payload?.officeId || null);
        }

        case C.NOTIFICATION_REMOTE_READ_ALL: {
            const scope = action.payload?.scope === "office" ? "office" : "personal";

            return markScopeRead(state, scope, action.payload?.officeId || null, action.payload?.category || null);
        }

        case C.NOTIFICATION_MARK_READ_FAILURE:
        case C.NOTIFICATION_MARK_ALL_READ_FAILURE:
            return {
                ...state,
                error: action.payload?.error || "Ошибка обновления уведомления",
            };

        default:
            return state;
    }
}
